import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosApiInstance } from "@/lib/axios";
import { API_PORTAL_URL } from "@/lib/config";

// ─── Client-side thunks (for Redux / Client Components) ─────────────────────

export const getHomeDetails = createAsyncThunk(
  "Website/HomePageData",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Website/HomePageData`,
      );
      return response.data?.Data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { Details?: string } } };
      console.error("Error Fetching Home Details:", error);
      return Promise.reject({
        message: err?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveSettings = createAsyncThunk(
  "Setting/FindAllActiveSetting",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Setting/FindAllActiveSetting`,
      );
      return response.data?.Data;
    } catch (error: any) {
      const err = error as { response?: { data?: { Details?: string } } };
      console.error("Error Fetching settings:", error);
      return Promise.reject({
        message: err?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllMetaData = createAsyncThunk(
  "SeoTags/FindAllSeoTags",
  async (FormData: unknown) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/FindAllSeoTags`,
        FormData,
      );
      return response.data?.Data ? response.data?.Data[0] : {};
    } catch (error: unknown) {
      const err = error as { response?: { data?: { Details?: string } } };
      console.error("Error Fetching Meta Data:", error);
      return Promise.reject({
        message: err?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveTestimonials = createAsyncThunk(
  "/Testimonial/FindAllActiveTestimonial",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Testimonial/FindAllActiveTestimonial`,
      );
      return response.data?.Data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { Details?: string } } };
      console.error("Error Fetching testimonials:", error);
      return Promise.reject({
        message: err?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveBanner = createAsyncThunk(
  "Banner/FindAllActiveBanner",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Banner/FindAllActiveBanner`,
      );
      return response.data?.Data || [];
    } catch (error: any) {
      const err = error as { response?: { data?: { Details?: string } } };
      console.error("Error Fetching Banner:", error);
      return Promise.reject({
        message: err?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── Server-side fetch functions (call ONLY from Server Components / page.tsx) ─

export async function fetchHomePageData() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Website/HomePageList`, {
      next: { revalidate: 30 },
    });
    const json = await res.json();
    return json.Data ?? null;
  } catch {
    return null;
  }
}

export async function fetchMetaData(PageURL: string) {
  try {
    const res = await fetch(`${API_PORTAL_URL}/SeoTags/FindAllSeoTags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ PageName: "", PageURL: PageURL }),
      next: { revalidate: 30 },
    });
    const json = await res.json();
    return json.Data?.[0] ?? null;
  } catch {
    return null;
  }
}

export async function getSeoMetadata(slug: string) {
  return fetchMetaData(slug);
}

export async function fetchBanners() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Banner/FindAllActiveBanner`, {
      next: { revalidate: 30 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchTestimonials() {
  try {
    const res = await fetch(
      `${API_PORTAL_URL}/Testimonial/FindAllActiveTestimonial`,
      {
        next: { revalidate: 30 },
      },
    );
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchCMSData(URL: string) {
  try {
    const res = await fetch(`${API_PORTAL_URL}/CMS/FindByURLCMS`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ CMSURL: URL }),
      next: { revalidate: 1 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchSettings() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Setting/FindAllActiveSetting`, {
      next: { revalidate: 30 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}
